import React, { useEffect, useState, useRef } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  ConfirmationResult,
  PhoneAuthProvider,
  linkWithCredential,
  signInWithPhoneNumber,
  RecaptchaVerifier
} from "firebase/auth";
import { auth, googleProvider } from "../../services/firebase";
import { logGTMEvent } from "../../services/googleAnalytics";
import { Link, useLocation, useNavigate } from "react-router-dom";
import TopBar from "../TopBar/TopBar";
import './Signup.scss';
import { Button, Form, Input, message, Typography } from "antd"
import { ppiApiAxios } from "../../services/api";
import { LockOutlined, MailOutlined, UserOutlined, GoogleOutlined } from '@ant-design/icons'
import { FirebaseError } from "firebase/app";

interface SignUpFormValues {
  fullName: string;
  confirmPassword: string;
  email: string;
  password: string;
  phoneNumber: string;
}

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ value = '', onChange }) => {
  const [country, setCountry] = useState('+1');
  const [number, setNumber] = useState('');

  const formatNumber = React.useCallback((raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (country === '+1') {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return digits;
  }, [country]);

  useEffect(() => {
    // Parse initial value if it exists
    if (value) {
      // Simple heuristic: assume country code is everything up to the first space or parenthesis, 
      // OR just assume +1 if it starts with +1, otherwise try to split?
      // Actually, since we store it combined, we need a way to split.
      // If we assume standard format +1XXXXXXXXXX, we can try to detect.

      // Let's stick to the previous logic: try to match known codes? 
      // But now code is editable.
      // Let's just assume if it starts with +, the first 2-4 chars might be code.
      // For now, let's just default to +1 and put the rest in number if we can't be sure.
      // Or better: if we have a previous value, we try to split it.

      // Since the user is typing, we usually drive from state -> onChange.
      // But for initial load (e.g. edit mode), we need to parse.
      // For this specific signup flow, it's usually empty initially.

      if (value.startsWith('+1')) {
        setCountry('+1');
        setNumber(formatNumber(value.slice(2)));
      } else if (value.startsWith('+')) {
        // Just take first 3 chars as code? +44, +52...
        // It's ambiguous without a list. 
        // Let's just take the first 3 chars for now if not +1
        setCountry(value.slice(0, 3));
        setNumber(value.slice(3));
      }
    }
  }, [value, formatNumber]); // Added value to dependency array to re-parse if value changes externally

  const triggerChange = (newCountry: string, newNumber: string) => {
    const cleanNumber = newNumber.replace(/\D/g, '');
    onChange?.(newCountry + cleanNumber);
  };

  const onCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    triggerChange(newCountry, number);
  };

  const onNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const digits = val.replace(/\D/g, '');
    const formatted = formatNumber(digits);
    setNumber(formatted);
    triggerChange(country, formatted);
  };

  const prefixInput = (
    <Input
      value={country}
      onChange={onCountryChange}
      style={{ width: 60, textAlign: 'center', padding: '4px 0' }}
      bordered={false}
    />
  );

  return (
    <Input
      addonBefore={prefixInput}
      value={number}
      onChange={onNumberChange}
      placeholder={country === '+1' ? "(555) 555-5555" : "5555555555"}
      size="large"
      maxLength={country === '+1' ? 14 : 20}
    />
  );
};

const SignUp: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [uiState, setUiState] = useState<'details' | 'verify_phone'>('details');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [tempFormData, setTempFormData] = useState<SignUpFormValues | null>(null);


  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    const container = document.getElementById('recaptcha-container');

    if (container) {
      // Safety check: clear container content before initializing to prevent duplicates
      container.innerHTML = '';

      if (!recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
            'size': 'invisible'
          });
        } catch (error) {
          console.error("Error initializing RecaptchaVerifier:", error);
        }
      }
    }

    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          // Ignore clear errors
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        if (uiState === 'details') {
          const redirectTo = new URLSearchParams(location.search).get("redirectTo") || "/";
          navigate(redirectTo, { replace: true });
        }
      }
    });
    return unsubscribe;
  }, [navigate, location.search, uiState]);

  const callBackendForUserCreation = async (data: { fullName: string, phone: string }) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken(true);
        await ppiApiAxios.post('/auth', { token, fullName: data.fullName, phone: data.phone });
      } catch (err) {
        messageApi.error("Failed to finalize account creation on the backend.");
      }
    }
  }

  const onFinish = async (values: SignUpFormValues) => {
    setLoading(true);
    setTempFormData(values);
    try {
      if (!recaptchaVerifierRef.current) throw new Error("RecaptchaVerifier not initialized");
      const confirmation = await signInWithPhoneNumber(auth, values.phoneNumber, recaptchaVerifierRef.current);
      setConfirmationResult(confirmation);
      setUiState('verify_phone');
      messageApi.info('A verification code has been sent to your phone.');
    } catch (err) {
      console.error("onFinish error:", err);
      messageApi.error("Failed to send verification code. Please check your phone number and try again.");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyCode = async (values: { code: string }) => {
    setLoading(true);
    if (!confirmationResult || !tempFormData) {
      messageApi.error("Verification session expired. Please refresh and try again.");
      setUiState('details');
      setLoading(false);
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, tempFormData.email, tempFormData.password);
      const phoneCredential = PhoneAuthProvider.credential(confirmationResult.verificationId, values.code);
      await linkWithCredential(userCredential.user, phoneCredential);
      await callBackendForUserCreation({ fullName: tempFormData.fullName, phone: tempFormData.phoneNumber });
      messageApi.success('Account created successfully! Redirecting...', 2);
      logGTMEvent('signup_success', { method: 'email_password' });
      // Google Ads conversion tracking for email/password signup
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', { 'send_to': 'AW-16761681511/4ABeCPPj7I4bEOfsy7g-' });
      }
      const redirectTo = new URLSearchParams(location.search).get("redirectTo") || "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      let errorMessage = "An error occurred.";
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMessage = 'That email address is already in use! Please start over.';
            setUiState('details');
            break;
          case 'auth/invalid-verification-code':
            errorMessage = 'The verification code is invalid. Please try again.';
            break;
          default:
            errorMessage = "Failed to create account. Please refresh and try again.";
            setUiState('details');
        }
      }
      messageApi.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await ppiApiAxios.post("/auth", { token: await result.user.getIdToken() });
      logGTMEvent('signup_success', { method: 'google_oauth' });
      // Google Ads conversion tracking for Google sign-in
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', { 'send_to': 'AW-16761681511/4ABeCPPj7I4bEOfsy7g-' });
      }
      const redirectTo = new URLSearchParams(location.search).get("redirectTo") || "/";
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      messageApi.error(`Google sign-in error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="page-container">
      {contextHolder}
      <TopBar className="title" title="Sign up" />
      <h1 className="subtitle">Your first report is free when you sign up!</h1>
      {uiState === 'details' ? (
        <>
          <Typography.Title level={2} className="heading">Sign Up</Typography.Title>
          <Form form={form} name="signup" onFinish={onFinish} layout="vertical" className="signup-form">
            <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
              <Button onClick={handleGoogleSignIn} className="google-signin-button" block size="large" style={{ marginBottom: '24px' }}>
                <GoogleOutlined className="google-icon" /> Sign In with Google
              </Button>
            </Form.Item>
            <Form.Item style={{ textAlign: 'center', marginBottom: '1rem' }}>OR</Form.Item>
            <Form.Item label="Full Name" name="fullName" rules={[{ required: true, message: 'Please input your Full Name!' }]}>
              <Input prefix={<UserOutlined />} placeholder="Full Name" size="large" />
            </Form.Item>
            <Form.Item label="Email" name="email" rules={[{ required: true, message: 'Please input your Email!' }, { type: 'email', message: 'Please enter a valid Email!' }]}>
              <Input prefix={<MailOutlined />} placeholder="Email" size="large" />
            </Form.Item>
            <Form.Item label="Phone Number" name="phoneNumber" rules={[{ required: true, message: 'A phone number is required.' }]}>
              <PhoneInput />
            </Form.Item>
            <Form.Item label="Password" name="password" rules={[{ required: true, message: 'Please input your Password!' }, { min: 6, message: 'Password must be at least 6 characters!' }]} hasFeedback>
              <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
            </Form.Item>
            <Form.Item label="Confirm Password" name="confirmPassword" dependencies={['password']} hasFeedback rules={[{ required: true, message: 'Please confirm your Password!' }, ({ getFieldValue }) => ({ validator(_, value) { if (!value || getFieldValue('password') === value) { return Promise.resolve(); } return Promise.reject(new Error('Passwords do not match!')); }, })]}>
              <Input.Password prefix={<LockOutlined />} placeholder="Confirm Password" size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">Sign Up</Button>
            </Form.Item>
            <Form.Item style={{ textAlign: 'center', marginBottom: 0 }}>
              <Typography.Text>Already have an account? <Link to={`/signin${location.search}`}><Typography.Link>Sign In</Typography.Link></Link></Typography.Text>
            </Form.Item>
          </Form>
        </>
      ) : (
        <div className="signup-form">
          <Typography.Title level={2} className="heading">Verify Phone</Typography.Title>
          <Typography.Text type="secondary" style={{ marginBottom: 24, display: 'block' }}>A 6-digit code was sent to the number you provided. Enter it below to continue.</Typography.Text>
          <Form onFinish={onVerifyCode}>
            <Form.Item name="code" rules={[{ required: true, message: 'Please input the verification code!' }]}>
              <Input placeholder="6-Digit Code" size="large" maxLength={6} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">Verify and Complete Sign Up</Button>
            </Form.Item>
          </Form>
        </div>
      )}


      <div id="recaptcha-container"></div>
    </div >
  );
};

export default SignUp;